<script setup lang="ts">
import type { TextVariant } from '../../Text/index.vue'
import type { User, UserMinified } from '@/types/user'

interface IProps {
  user: User | UserMinified
  variant?: 'default' | 'text' | 'big'
  openInNewTab?: boolean
  shouldRedirect?: boolean
  color?: string
  avatarSize?: number
  textVariant?: TextVariant
  hideText?: boolean
  showBorders?: boolean
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    variant: 'default',
    shouldRedirect: true,
    avatarSize: 16,
    textVariant: 'caption',
  },
)

const { user, variant, avatarSize, textVariant, hideText } = toRefs(props)

const { colors } = useTheme()

const isDefault = computed(() => variant.value === 'default')
const isBig = computed(() => variant.value === 'big')
const isText = computed(() => variant.value === 'text')
const textColor = computed(() => props.color || (isDefault.value ? colors.softBlack : colors.bluishDarker))

const userName = computed(() => formatUserName(user))

const optimizedAvatar = computed(() => optimizeImage(user.value.avatar, {
  width: ((isDefault.value || isBig.value) ? 20 : avatarSize.value) * 1.6,
  height: ((isDefault.value || isBig.value) ? 20 : avatarSize.value) * 1.6,
}))
</script>

<template>
  <NuxtLink :to="shouldRedirect ? `/${user.username}` : ''" :target="openInNewTab ? '_blank' : '_self'" @click.stop>
    <div
      :gap="variant === 'big' ? '6px' : '1'"
      class="flex items-center rounded-full transition-colors"
      :class="{
        'pl-1 pr-2 h-7 hover:bg-soft-gray': isDefault,
        'pl-2 pr-3 h-10 hover:bg-soft-gray': isBig,
        'pl-0': isText,
      }"
    >
      <div
        class="rounded-full bg-white flex-shrink-0"
        :class="{
          'b-1 border-soft-gray': showBorders,
          'p-[1px]': showBorders && !isText,
        }"
      >
        <img
          loading="lazy"
          :src="optimizedAvatar"
          referrerpolicy="no-referrer"
          object-cover object-center rounded-full aspect-square
          :alt="`${user.name} profile image`"
          :title="user.name"
          :w="`${avatarSize}px`" :h="`${avatarSize}px`"
          :width="avatarSize" :height="avatarSize"
        >
      </div>

      <TextV2
        v-if="variant === 'big' && !hideText"
        class="body-14 font-bold text-soft-black truncate !inline-block !overflow-hidden"
      >
        {{ userName }}
      </TextV2>
      <Text
        v-else-if="!hideText"
        :variant="textVariant"
        :color="textColor"
        bold
        class="truncate !inline-block !overflow-hidden"
      >
        {{ userName }}
      </Text>

      <slot v-if="$slots.suffix" name="suffix" />
    </div>
  </NuxtLink>
</template>
